import React, { useEffect, useState, useMemo } from 'react';
import { Form, Input, Button, Space, Typography, message, Divider, Card } from 'antd';
import { SearchOutlined, SaveOutlined, RocketOutlined } from '@ant-design/icons';
import CommonForm from '../../../global/form';
import CommonTable from '../../../global/table';
import CommonCheckbox from '../../../global/checkbox';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';

const { Title, Text } = Typography;

const EnhancedTripleseat = ({ destinations, activeDestination, btnClassName }) => {
    const [form] = Form.useForm();
    const [locations, setLocations] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);

    const DESTINATION_TRIPLESEAT = SOURCES.DESTINATION_TRIPPLE_SEAT || "tripleseat";

    // Mutation for fetching locations
    const { mutate: getLocations, isPending: isLocLoading } = useGenericMutation({
        endpoint: "integration/tripleseat-locations",
        method: "POST",
        onSuccess: (data) => {
            setLocations(data?.data || []);
            message.success("Locations fetched successfully");
        },
        onError: () => message.error("Error getting TripleSeat locations")
    });

    // Mutation for saving configuration
    const { mutate: saveTripleSeat, isPending: isSaving } = useGenericMutation({
        endpoint: "integration",
        method: "POST",
        onSuccess: () => {
            message.success("Tripleseat configuration saved successfully");
        }
    });

    useEffect(() => {
        if (destinations) {
            const config = destinations.find(d => d.integrationCompany === DESTINATION_TRIPLESEAT);
            if (config) {
                form.setFieldsValue({
                    publicKey: config.publicKey,
                    sendLeadsLimit: config.leadSentConfig?.sentLimit || 0
                });
                if (config.locations) setSelectedLocations(config.locations);
                if (config.publicKey) getLocations({ key: config.publicKey });
            }
        }
    }, [destinations, form]);

    const onFinish = (values) => {
        const payload = {
            publicKey: values.publicKey,
            sendLeadsLimit: values.sendLeadsLimit,
            integrationCompany: DESTINATION_TRIPLESEAT,
            locations: selectedLocations,
        };
        saveTripleSeat(payload);
    };

    const handleLocationSelect = (id, checked) => {
        if (checked) {
            setSelectedLocations(prev => [...prev, id.toString()]);
        } else {
            setSelectedLocations(prev => prev.filter(locId => locId !== id.toString()));
        }
    };

    const columns = [
        {
            title: "Select",
            dataIndex: "id",
            key: "id",
            width: 80,
            render: (id) => (
                <CommonCheckbox
                    checked={selectedLocations.includes(id.toString())}
                    onChange={(e) => handleLocationSelect(id, e.target.checked)}
                />
            )
        },
        {
            title: "Location Name",
            dataIndex: "name",
            key: "name",
            render: (text) => <Text strong>{text}</Text>
        }
    ];


    const formData = useMemo(() => [
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Public Key",
            name: "publicKey",
            placeholder: "Enter Tripleseat Public Key",
            required: true,
            span: 24,
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,
            component: (
                <Button
                    type="dashed"
                    icon={isLocLoading ? null : <SearchOutlined />}
                    onClick={() => {
                        const key = form.getFieldValue("publicKey");
                        if (key) getLocations({ key });
                        else message.warning("Please enter Public Key first");
                    }}
                    className="h-10 w-full border-dashed hover:border-primary hover:!text-primary"
                >
                    {isLocLoading && <Spinner className={"!border-primary"} />}
                    Get Locations
                </Button>
            )
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,
            component: locations.length > 0 && (
                <div className="mb-0 animate-in slide-in-from-top-2 duration-300">
                    <Text type="secondary" className="block mb-2 text-xs font-semibold uppercase tracking-wider">Available Locations</Text>
                    <CommonTable
                        columns={columns}
                        data={locations}
                        showActions={false}
                    />
                </div>
            )
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: "Initial Lead Send Limit",
            name: "sendLeadsLimit",
            placeholder: "How many leads to send initially?",
            type: "number",
            span: 24,
        }
    ], [locations, selectedLocations, isLocLoading, form]);

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white shadow-sm border border-gray-100 flex items-center justify-center p-3">
                    <img src={activeDestination.icon} alt={activeDestination.title} className="w-full h-full object-contain" />
                </div>
                <div>
                    <Title level={3} className="!mb-1">{activeDestination.title} Configuration</Title>
                    <Text type="secondary">Manage your Tripleseat integration settings and locations.</Text>
                </div>
            </div>

            <Card className="rounded-2xl border-none shadow-sm transition-all">
                <CommonForm
                    form={form}
                    data={formData}
                    onFinish={onFinish}
                    layoutType="vertical"
                />

                <Divider className="mb-4 border-gray-100" />

                <div className="flex justify-start">
                    <Button
                        size="large"
                        icon={isSaving ? null : <SaveOutlined />}
                        onClick={() => form.submit()}
                        className={cn(btnClassName)}
                    >
                        {isSaving && <Spinner className={"!border-white"} />}
                        Save Configuration
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default EnhancedTripleseat;
